import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isHelpdeskMode } from '../../utils/helpdeskMode';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Badge,
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  ChatBubbleOutline as ReplyIcon,
  MoreVert as MoreIcon,
  Timer as TimerIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  EventBusy as NoDueDateIcon,
  Pause as OnHoldIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO, differenceInHours, differenceInMinutes, isPast } from 'date-fns';
import api, { extractData } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface QueueTicket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  priority: string;
  due_date: string | null;
  requester_email: string;
  requester_name: string;
  is_external: boolean;
  created_at: string;
  updated_at: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  assigned_email: string;
  assigned_first_name: string;
  assigned_last_name: string;
  reply_count: string;
  public_reply_count: string;
}

interface Agent {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

const priorityColors: Record<string, string> = {
  urgent: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
};

const statusColors: Record<string, string> = {
  open: '#3b82f6',
  in_progress: '#f59e0b',
  resolved: '#10b981',
  closed: '#6b7280',
  on_hold: '#8b5cf6',
};

const HelpdeskAgentQueue: React.FC = () => {
  const navigate = useNavigate();
  const basePath = isHelpdeskMode() ? '/support' : '/helpdesk';
  const { user } = useAuthStore();
  const [tickets, setTickets] = useState<QueueTicket[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [countdownMode, setCountdownMode] = useState(true);
  const [contextMenu, setContextMenu] = useState<{ el: HTMLElement; ticket: QueueTicket } | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const agentParam = selectedAgent !== 'all' ? `?agentId=${selectedAgent}` : '';
      const [queueRes, agentsRes] = await Promise.all([
        api.get(`/support/customers/queue${agentParam}`),
        api.get('/support/customers/agents'),
      ]);
      setTickets(extractData(queueRes) || []);
      setAgents(extractData(agentsRes) || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedAgent]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [selectedAgent]);

  const categorized = useMemo(() => {
    const now = new Date();
    const overdue: QueueTicket[] = [];
    const dueIn1h: QueueTicket[] = [];
    const dueIn6h: QueueTicket[] = [];
    const noDueDate: QueueTicket[] = [];
    const onHold: QueueTicket[] = [];

    tickets.forEach((t) => {
      if (t.status === 'on_hold') {
        onHold.push(t);
        return;
      }
      if (!t.due_date) {
        noDueDate.push(t);
        return;
      }
      const due = parseISO(t.due_date);
      if (isPast(due)) {
        overdue.push(t);
      } else if (differenceInHours(due, now) < 1) {
        dueIn1h.push(t);
      } else {
        dueIn6h.push(t);
      }
    });

    return { overdue, dueIn1h, dueIn6h, noDueDate, onHold };
  }, [tickets]);

  const columns = [
    { key: 'dueIn6h', label: `Due in 6 hours and more`, color: '#1e293b', tickets: categorized.dueIn6h, icon: <ScheduleIcon /> },
    { key: 'overdue', label: 'Overdue', color: '#ef4444', tickets: categorized.overdue, icon: <WarningIcon /> },
    { key: 'dueIn1h', label: 'Due in 1 hour', color: '#f59e0b', tickets: categorized.dueIn1h, icon: <TimerIcon /> },
    { key: 'noDueDate', label: 'No Due Date', color: '#3b82f6', tickets: categorized.noDueDate, icon: <NoDueDateIcon /> },
    { key: 'onHold', label: 'On Hold', color: '#8b5cf6', tickets: categorized.onHold, icon: <OnHoldIcon /> },
  ];

  const getCountdown = (dueDate: string | null) => {
    if (!dueDate) return null;
    const due = parseISO(dueDate);
    const now = new Date();
    if (isPast(due)) {
      const mins = Math.abs(differenceInMinutes(due, now));
      if (mins < 60) return `${mins}m overdue`;
      const hrs = Math.floor(mins / 60);
      return `${hrs}h ${mins % 60}m overdue`;
    }
    const mins = differenceInMinutes(due, now);
    if (mins < 60) return `${mins}m left`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m left`;
  };

  const getRequesterName = (t: QueueTicket) => {
    if (t.requester_name) return t.requester_name;
    if (t.user_first_name) return `${t.user_first_name} ${t.user_last_name || ''}`.trim();
    return t.requester_email || t.user_email || 'Unknown';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleSetDueDate = async (ticket: QueueTicket, hours: number | null) => {
    try {
      const dueDate = hours !== null ? new Date(Date.now() + hours * 3600000).toISOString() : null;
      await api.put(`/support/customers/tickets/${ticket.id}/due-date`, { dueDate });
      fetchData();
    } catch (e) { /* ignore */ }
    setContextMenu(null);
  };

  if (loading && tickets.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        px: 3, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 2,
        bgcolor: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              sx={{ fontSize: '0.85rem', bgcolor: '#f9fafb', borderRadius: '8px' }}
            >
              <MenuItem value="all">All Agents ({tickets.length})</MenuItem>
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.firstName ? `${a.firstName} ${a.lastName || ''}`.trim() : a.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchData}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1 }} />

        <Chip
          label={countdownMode ? 'Countdown Mode' : 'Date Mode'}
          icon={<ClockIcon />}
          onClick={() => setCountdownMode(!countdownMode)}
          variant={countdownMode ? 'filled' : 'outlined'}
          color="primary"
          size="small"
          sx={{ cursor: 'pointer' }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }}>{error}</Alert>
      )}

      {/* Kanban Board */}
      <Box sx={{
        flex: 1, display: 'flex', gap: 0, overflow: 'auto',
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
      }}>
        {columns.map((col) => (
          <Box
            key={col.key}
            sx={{
              flex: 1, minWidth: 260, maxWidth: 340,
              display: 'flex', flexDirection: 'column',
              borderRight: '1px solid #e5e7eb',
              bgcolor: col.tickets.length > 0 ? '#fff' : '#f8fafc',
            }}
          >
            {/* Column Header */}
            <Box sx={{
              px: 2, py: 1.5,
              display: 'flex', alignItems: 'center', gap: 1,
              borderBottom: '2px solid',
              borderColor: col.color,
              bgcolor: '#fff',
              flexShrink: 0,
            }}>
              <Typography sx={{ color: col.color, fontWeight: 700, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                {col.label}
              </Typography>
              {col.tickets.length > 0 && (
                <Typography sx={{ color: col.color, fontWeight: 700, fontSize: '0.82rem' }}>
                  ({col.tickets.length})
                </Typography>
              )}
            </Box>

            {/* Column Content */}
            <Box sx={{
              flex: 1, overflow: 'auto', p: 1.5,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 },
            }}>
              {col.tickets.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
                  <Box
                    component="img"
                    src="/queue-empty.svg"
                    onError={(e: any) => { e.target.style.display = 'none'; }}
                    sx={{ width: 80, height: 80, mb: 2, opacity: 0.3 }}
                  />
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                    No tickets in this Queue
                  </Typography>
                </Box>
              ) : (
                col.tickets.map((ticket) => {
                  const countdown = getCountdown(ticket.due_date);
                  const isOverdue = ticket.due_date && isPast(parseISO(ticket.due_date));
                  return (
                    <Paper
                      key={ticket.id}
                      elevation={0}
                      sx={{
                        mb: 1.5, p: 2, borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: '#93c5fd', boxShadow: '0 2px 8px rgba(59,130,246,0.08)' },
                      }}
                      onClick={() => navigate(`${basePath}/tickets/${ticket.id}`)}
                    >
                      {/* Ticket Subject */}
                      <Typography sx={{
                        fontWeight: 600, fontSize: '0.88rem', color: '#1e293b',
                        mb: 0.75, lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {ticket.subject}
                      </Typography>

                      {/* Meta row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 500 }}>
                          #{ticket.ticket_number}
                        </Typography>
                        <Typography sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>·</Typography>
                        <Typography sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                          {getRequesterName(ticket)}
                        </Typography>
                        {ticket.is_external && (
                          <Tooltip title="External ticket">
                            <EmailIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                          </Tooltip>
                        )}
                      </Box>

                      {/* Date + countdown */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <ClockIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                        <Typography sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
                          {countdownMode && countdown
                            ? countdown
                            : formatDistanceToNow(parseISO(ticket.created_at), { addSuffix: true })}
                        </Typography>
                      </Box>

                      {/* Bottom row: status + priority + replies */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Chip
                          label={ticket.status.replace('_', ' ')}
                          size="small"
                          sx={{
                            height: 22, fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize',
                            bgcolor: `${statusColors[ticket.status] || '#6b7280'}18`,
                            color: statusColors[ticket.status] || '#6b7280',
                          }}
                        />
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: priorityColors[ticket.priority] || '#eab308',
                        }} />
                        <Box sx={{ flex: 1 }} />
                        {parseInt(ticket.reply_count || '0') > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <ReplyIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                            <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                              {ticket.public_reply_count || ticket.reply_count}
                            </Typography>
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setContextMenu({ el: e.currentTarget, ticket }); }}
                          sx={{ p: 0.3 }}
                        >
                          <MoreIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                        </IconButton>
                      </Box>
                    </Paper>
                  );
                })
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Context menu for due date */}
      <Menu
        anchorEl={contextMenu?.el}
        open={Boolean(contextMenu)}
        onClose={() => setContextMenu(null)}
        PaperProps={{ sx: { borderRadius: '10px', minWidth: 180 } }}
      >
        <MenuItem disabled sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Set Due Date</MenuItem>
        <MenuItem onClick={() => contextMenu && handleSetDueDate(contextMenu.ticket, 1)} sx={{ fontSize: '0.85rem' }}>
          In 1 hour
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleSetDueDate(contextMenu.ticket, 4)} sx={{ fontSize: '0.85rem' }}>
          In 4 hours
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleSetDueDate(contextMenu.ticket, 8)} sx={{ fontSize: '0.85rem' }}>
          In 8 hours
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleSetDueDate(contextMenu.ticket, 24)} sx={{ fontSize: '0.85rem' }}>
          In 24 hours
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleSetDueDate(contextMenu.ticket, 72)} sx={{ fontSize: '0.85rem' }}>
          In 3 days
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleSetDueDate(contextMenu.ticket, null)} sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
          Remove due date
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default HelpdeskAgentQueue;
